import { useEffect, useRef, useState, useMemo } from 'react';
import { MapContainer, TileLayer, Polygon, Marker, Polyline, useMap } from 'react-leaflet';
import { CtrlScrollZoom } from './CtrlScrollZoom';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  EsriVectorBasemap,
  soilSurveyTileUrl,
  SOIL_ATTRIBUTION,
} from '@/lib/basemap';

export interface WellData {
  id: string;
  name: string;
  lat: number;
  lon: number;
  type: 'existing' | 'predicted';
  depth?: number;
  concentration?: number;
  installCost?: number;
  breachMonth?: number | null;
  // Concentration prediction fields (from ConcentrationPredictor)
  exceedanceProbability?: number;    // P(next sample > MCL), 0-1
  predictedConcMgl?: number;        // Predicted concentration in mg/L
  mclMgl?: number;                  // Regulatory limit in mg/L
  concTrend?: 'increasing' | 'decreasing' | 'stable';
}

export interface ConcentrationContour {
  level: number; // 0.9 = 90%, 0.5 = 50%, etc.
  concentration_ug_L: number;
  coordinates: [number, number][];
  area_sqft: number;
}

export interface PlumeContourGeo {
  month: number;
  coordinates: [number, number][]; // [lat, lon] pairs - outer contour
  area: number;
  concentration?: number;
  color: string;
  // NEW: All concentration contours for this time step
  allContours?: ConcentrationContour[];
}

export interface HydrogeologyData {
  hydraulic_conductivity_ft_day: number;
  porosity: number;
  hydraulic_gradient: number;
  flow_direction_degrees: number;
  depth_to_water_ft: number;
  soil_type: string;
  aquifer_name?: string;
  groundwater_velocity_ft_day?: number;
}

export interface PlumeMapSatelliteProps {
  centerLat: number;
  centerLon: number;
  currentPlume: [number, number][]; // [lat, lon] pairs
  predictedContours: PlumeContourGeo[];
  existingWells: WellData[];
  predictedWells: WellData[];
  selectedMonth: number;
  onWellClick?: (well: WellData) => void;
  showPredictions?: boolean;
  gradient?: { direction: number; magnitude: number };
  bounds?: {
    min_lat: number;
    max_lat: number;
    min_lon: number;
    max_lon: number;
  };
  // NEW: Hydrogeology data for parameter panel
  hydrogeology?: HydrogeologyData;
  // NEW: All plume states for trajectory
  allPlumeStates?: Array<{
    time_months: number;
    centroid_lat: number;
    centroid_lon: number;
    migration_distance_ft: number;
  }>;
  // NEW: Max concentration for color scaling
  maxConcentration?: number;
}

// Concentration-based color scheme (hot to cool)
const CONCENTRATION_COLORS = {
  core: { fill: '#dc2626', stroke: '#991b1b', label: '90%+ (Core)' },      // Red
  high: { fill: '#f97316', stroke: '#c2410c', label: '50-90% (High)' },    // Orange
  medium: { fill: '#eab308', stroke: '#a16207', label: '10-50% (Medium)' }, // Yellow
  low: { fill: '#06b6d4', stroke: '#0891b2', label: '1-10% (Outer)' },      // Cyan
};

// Get color based on concentration level
function getContourColor(level: number): { fill: string; stroke: string } {
  if (level >= 0.9) return CONCENTRATION_COLORS.core;
  if (level >= 0.5) return CONCENTRATION_COLORS.high;
  if (level >= 0.1) return CONCENTRATION_COLORS.medium;
  return CONCENTRATION_COLORS.low;
}

// Create icons using L.divIcon
const sourceIcon = L.divIcon({
  className: 'source-icon',
  html: `<div style="
    width: 24px;
    height: 24px;
    background: linear-gradient(135deg, #ef4444 0%, #b91c1c 100%);
    border: 3px solid #fff;
    border-radius: 50%;
    box-shadow: 0 0 12px rgba(239,68,68,0.6), 0 2px 8px rgba(0,0,0,0.4);
    animation: pulse 2s infinite;
    will-change: transform, opacity;
  "></div>
  <style>
    @keyframes pulse {
      0%, 100% { transform: scale(1); opacity: 1; }
      50% { transform: scale(1.1); opacity: 0.8; }
    }
    .map-offscreen * { animation-play-state: paused !important; }
  </style>`,
  iconSize: [24, 24],
  iconAnchor: [12, 12],
});

const createWellIcon = (color: string, isBreached: boolean = false, isRecommended: boolean = false) => {
  const size = isRecommended ? 16 : 14;
  const borderWidth = isBreached ? 3 : 2;
  const borderColor = isBreached ? '#ef4444' : '#fff';
  const shadow = isBreached ? '0 0 8px rgba(239,68,68,0.6),' : '';

  return L.divIcon({
    className: 'custom-well-icon',
    html: `<div style="
      width: ${size}px;
      height: ${size}px;
      background: ${color};
      border: ${borderWidth}px solid ${borderColor};
      border-radius: 50%;
      box-shadow: ${shadow} 0 2px 6px rgba(0,0,0,0.4);
      ${isRecommended ? 'animation: recommend-pulse 1.5s infinite; will-change: box-shadow;' : ''}
    "></div>
    ${isRecommended ? `<style>
      @keyframes recommend-pulse {
        0%, 100% { box-shadow: 0 0 4px ${color}; }
        50% { box-shadow: 0 0 12px ${color}; }
      }
    </style>` : ''}`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
  });
};

// Exceedance-aware well icon: shows a ring color based on P(exceed MCL)
const createExceedanceWellIcon = (exceedanceProb: number, isBreached: boolean = false) => {
  const size = 18;
  // Color: green (<0.3), amber (0.3-0.7), red (>0.7)
  const fillColor = exceedanceProb > 0.7 ? '#ef4444'
    : exceedanceProb > 0.3 ? '#f59e0b'
    : '#10b981';
  const glowColor = exceedanceProb > 0.7 ? 'rgba(239,68,68,0.5)'
    : exceedanceProb > 0.3 ? 'rgba(245,158,11,0.4)'
    : 'rgba(16,185,129,0.3)';
  const borderColor = isBreached ? '#ef4444' : '#fff';
  const pctLabel = Math.round(exceedanceProb * 100);

  return L.divIcon({
    className: 'exceedance-well-icon',
    html: `<div style="
      position: relative;
      width: ${size}px;
      height: ${size}px;
    ">
      <div style="
        width: ${size}px;
        height: ${size}px;
        background: ${fillColor};
        border: 2px solid ${borderColor};
        border-radius: 50%;
        box-shadow: 0 0 8px ${glowColor}, 0 2px 6px rgba(0,0,0,0.4);
      "></div>
      <div style="
        position: absolute;
        top: ${size + 1}px;
        left: 50%;
        transform: translateX(-50%);
        font-size: 9px;
        font-weight: 700;
        color: ${fillColor};
        white-space: nowrap;
        text-shadow: 0 0 3px rgba(0,0,0,0.8);
        font-family: monospace;
      ">${pctLabel}%</div>
    </div>`,
    iconSize: [size, size + 14],
    iconAnchor: [size / 2, size / 2],
  });
};

// Flow direction arrow icon
const createFlowArrow = (direction: number, magnitude: number) => {
  const arrowLength = Math.min(60, Math.max(30, magnitude * 100));
  const rotation = direction - 90; // CSS rotation is from East, we need from North

  return L.divIcon({
    className: 'flow-arrow',
    html: `<div style="
      width: ${arrowLength}px;
      height: 20px;
      position: relative;
      transform: rotate(${rotation}deg);
      transform-origin: left center;
    ">
      <div style="
        position: absolute;
        top: 8px;
        left: 0;
        width: ${arrowLength - 10}px;
        height: 4px;
        background: linear-gradient(90deg, rgba(59,130,246,0.8) 0%, rgba(59,130,246,0.3) 100%);
        border-radius: 2px;
      "></div>
      <div style="
        position: absolute;
        top: 5px;
        right: 0;
        width: 0;
        height: 0;
        border-left: 12px solid rgba(59,130,246,0.9);
        border-top: 5px solid transparent;
        border-bottom: 5px solid transparent;
      "></div>
    </div>`,
    iconSize: [arrowLength, 20],
    iconAnchor: [0, 10],
  });
};

// Component to auto-fit bounds
function FitBounds({ bounds }: { bounds: L.LatLngBoundsExpression }) {
  const map = useMap();
  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds, { padding: [30, 30] });
    }
  }, [bounds, map]);
  return null;
}

// Flow vector field component
function FlowVectorField({
  centerLat,
  centerLon,
  direction,
  magnitude,
  extent
}: {
  centerLat: number;
  centerLon: number;
  direction: number;
  magnitude: number;
  extent: number; // in feet
}) {
  // Create a grid of flow arrows
  const arrows = useMemo(() => {
    const result: Array<{ lat: number; lon: number }> = [];
    const gridSize = 3; // 3x3 grid
    const spacing = extent / 364000 / gridSize; // Convert feet to degrees

    for (let i = -1; i <= 1; i++) {
      for (let j = -1; j <= 1; j++) {
        if (i === 0 && j === 0) continue; // Skip center (source is there)
        result.push({
          lat: centerLat + i * spacing,
          lon: centerLon + j * spacing * Math.cos(centerLat * Math.PI / 180),
        });
      }
    }
    return result;
  }, [centerLat, centerLon, extent]);

  return (
    <>
      {arrows.map((pos, idx) => (
        <Marker
          key={`flow-${idx}`}
          position={[pos.lat, pos.lon]}
          icon={createFlowArrow(direction, magnitude)}
          interactive={false}
        />
      ))}
    </>
  );
}

export function PlumeMapSatellite({
  centerLat,
  centerLon,
  currentPlume,
  predictedContours,
  existingWells,
  predictedWells,
  selectedMonth,
  onWellClick,
  showPredictions = true,
  gradient,
  bounds,
  hydrogeology,
  allPlumeStates,
  maxConcentration = 1000,
}: PlumeMapSatelliteProps) {
  const [selectedWell, setSelectedWell] = useState<WellData | null>(null);
  const [showFlowField, setShowFlowField] = useState(true);
  const [showAllContours, setShowAllContours] = useState(true);
  const [showSoilOverlay, setShowSoilOverlay] = useState(false);  // Soil survey OFF by default - can be toggled on
  const [isMapVisible, setIsMapVisible] = useState(true);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Pause CSS animations when map is off-screen to save GPU
  useEffect(() => {
    const el = mapContainerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsMapVisible(entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Validate inputs
  if (!centerLat || !centerLon || isNaN(centerLat) || isNaN(centerLon)) {
    return (
      <div className="w-full h-[600px] bg-mono-950 rounded-lg flex items-center justify-center border border-mono-700">
        <p className="text-mono-500 text-sm">Invalid coordinates</p>
      </div>
    );
  }

  // Calculate map bounds with safety checks
  const mapBounds: L.LatLngBoundsExpression = bounds &&
    !isNaN(bounds.min_lat) && !isNaN(bounds.max_lat) && !isNaN(bounds.min_lon) && !isNaN(bounds.max_lon)
    ? [[bounds.min_lat, bounds.min_lon], [bounds.max_lat, bounds.max_lon]]
    : [[centerLat - 0.01, centerLon - 0.01], [centerLat + 0.01, centerLon + 0.01]];

  // Get the selected month's contour data
  const selectedContour = showPredictions && Array.isArray(predictedContours)
    ? predictedContours.find(c => c && c.month === selectedMonth)
    : null;

  // Get all concentration contours for the selected month
  const concentrationContours = useMemo(() => {
    if (!selectedContour?.allContours) {
      // Fallback: create synthetic contours from outer contour if allContours not available
      if (selectedContour?.coordinates && selectedContour.coordinates.length >= 3) {
        return [
          { level: 0.01, coordinates: selectedContour.coordinates, concentration_ug_L: maxConcentration * 0.01, area_sqft: selectedContour.area },
        ];
      }
      return [];
    }
    return selectedContour.allContours;
  }, [selectedContour, maxConcentration]);

  // Baseline contour for reference
  const baselineContour = showPredictions && Array.isArray(predictedContours) && selectedMonth > 6
    ? predictedContours.find(c => c && c.month === 6 && Array.isArray(c.coordinates) && c.coordinates.length >= 3)
    : null;

  // Migration trajectory line
  const trajectoryLine = useMemo(() => {
    if (!allPlumeStates || allPlumeStates.length < 2) return null;
    return allPlumeStates
      .filter(s => s.time_months <= selectedMonth)
      .map(s => [s.centroid_lat, s.centroid_lon] as [number, number]);
  }, [allPlumeStates, selectedMonth]);

  // Calculate flow direction and magnitude
  const flowDirection = gradient?.direction ?? hydrogeology?.flow_direction_degrees ?? 180;
  const flowMagnitude = gradient?.magnitude ?? (hydrogeology?.hydraulic_gradient ?? 0.005);
  const groundwaterVelocity = hydrogeology
    ? (hydrogeology.hydraulic_conductivity_ft_day * hydrogeology.hydraulic_gradient / hydrogeology.porosity)
    : 0;

  // Plume extent for flow field sizing
  const plumeExtent = selectedContour?.area ? Math.sqrt(selectedContour.area) * 2 : 500;

  return (
    <div ref={mapContainerRef} className={`w-full rounded-lg overflow-hidden border border-mono-700 bg-mono-950${!isMapVisible ? ' map-offscreen' : ''}`}>
      {/* Hydrogeology Parameter Panel */}
      {hydrogeology && (
        <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-b border-mono-700">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-mono-300 uppercase tracking-wider">Hydrogeology Parameters</span>
            <div className="flex items-center gap-4">
              {/* Layer Toggles */}
              <div className="flex gap-2">
                <button
                  onClick={() => setShowFlowField(!showFlowField)}
                  className={`px-2 py-1 text-xs rounded ${showFlowField ? 'bg-blue-500/20 text-blue-400 border border-blue-500/50' : 'bg-mono-800 text-mono-500 border border-mono-700'}`}
                >
                  Flow Field
                </button>
                <button
                  onClick={() => setShowAllContours(!showAllContours)}
                  className={`px-2 py-1 text-xs rounded ${showAllContours ? 'bg-orange-500/20 text-orange-400 border border-orange-500/50' : 'bg-mono-800 text-mono-500 border border-mono-700'}`}
                >
                  Concentration Zones
                </button>
                <button
                  onClick={() => setShowSoilOverlay(!showSoilOverlay)}
                  className={`px-2 py-1 text-xs rounded ${showSoilOverlay ? 'bg-green-500/20 text-green-400 border border-green-500/50' : 'bg-mono-800 text-mono-500 border border-mono-700'}`}
                >
                  Soil Survey
                </button>
              </div>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">K (ft/day)</div>
              <div className="text-sm font-mono text-cyan-400">{hydrogeology.hydraulic_conductivity_ft_day.toFixed(1)}</div>
            </div>
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">Porosity</div>
              <div className="text-sm font-mono text-cyan-400">{(hydrogeology.porosity * 100).toFixed(0)}%</div>
            </div>
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">Gradient</div>
              <div className="text-sm font-mono text-cyan-400">{hydrogeology.hydraulic_gradient.toFixed(4)}</div>
            </div>
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">GW Velocity</div>
              <div className="text-sm font-mono text-emerald-400">{groundwaterVelocity.toFixed(2)} ft/day</div>
            </div>
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">Flow Dir</div>
              <div className="text-sm font-mono text-blue-400">{flowDirection.toFixed(0)}° {getCardinalDirection(flowDirection)}</div>
            </div>
            <div className="bg-mono-800/50 rounded px-2 py-1.5">
              <div className="text-[10px] text-mono-500 uppercase">Depth to GW</div>
              <div className="text-sm font-mono text-amber-400">{hydrogeology.depth_to_water_ft.toFixed(0)} ft</div>
            </div>
          </div>
          {hydrogeology.soil_type && (
            <div className="mt-2 flex items-center gap-2">
              <span className="text-[10px] text-mono-500 uppercase">Aquifer:</span>
              <span className="text-xs text-mono-300 bg-mono-800 px-2 py-0.5 rounded">{hydrogeology.soil_type}</span>
            </div>
          )}
        </div>
      )}

      <MapContainer
        center={[centerLat, centerLon]}
        zoom={15}
        style={{ height: '600px', width: '100%' }}
        scrollWheelZoom={false}
      >
        <CtrlScrollZoom />
        {/* Base Map — Esri vector imagery + labels via MapLibre. */}
        <EsriVectorBasemap />

        {/* USDA NRCS Soil Survey Overlay (via Esri) — only show when enabled */}
        {showSoilOverlay && (
          <TileLayer
            key="soil-overlay"
            url={soilSurveyTileUrl()}
            attribution={SOIL_ATTRIBUTION}
            maxZoom={19}
            opacity={0.35}
          />
        )}

        {/* Fit to bounds */}
        <FitBounds bounds={mapBounds} />

        {/* Migration trajectory line */}
        {trajectoryLine && trajectoryLine.length > 1 && (
          <Polyline
            positions={trajectoryLine}
            pathOptions={{
              color: '#f59e0b',
              weight: 3,
              dashArray: '8, 8',
              opacity: 0.8,
            }}
          />
        )}

        {/* Baseline contour (6 month) shown as dashed outline for reference */}
        {baselineContour && (
          <Polygon
            key="baseline-contour"
            positions={baselineContour.coordinates}
            pathOptions={{
              color: '#94a3b8',
              fillColor: 'transparent',
              fillOpacity: 0,
              weight: 1.5,
              dashArray: '6, 6',
            }}
          />
        )}

        {/* Concentration contours - render from outer to inner (so inner is on top) */}
        {showAllContours && concentrationContours
          .slice()
          .sort((a, b) => a.level - b.level) // Sort ascending so we render outer first
          .map((contour, idx) => {
            const colors = getContourColor(contour.level);
            const opacity = 0.15 + (contour.level * 0.4); // Higher concentration = more opaque

            return (
              <Polygon
                key={`contour-${selectedMonth}-${idx}-${contour.level}`}
                positions={contour.coordinates}
                pathOptions={{
                  color: colors.stroke,
                  fillColor: colors.fill,
                  fillOpacity: opacity,
                  weight: contour.level >= 0.5 ? 2 : 1.5,
                }}
              />
            );
          })
        }

        {/* If not showing all contours, show just the outer one */}
        {!showAllContours && selectedContour?.coordinates && selectedContour.coordinates.length >= 3 && (
          <Polygon
            positions={selectedContour.coordinates}
            pathOptions={{
              color: '#06b6d4',
              fillColor: '#06b6d4',
              fillOpacity: 0.25,
              weight: 2,
            }}
          />
        )}

        {/* Current plume (actual measured) */}
        {Array.isArray(currentPlume) && currentPlume.length >= 3 && (
          <Polygon
            positions={currentPlume}
            pathOptions={{
              color: '#dc2626',
              fillColor: '#dc2626',
              fillOpacity: 0.5,
              weight: 3,
            }}
          />
        )}

        {/* Flow vector field */}
        {showFlowField && hydrogeology && (
          <FlowVectorField
            centerLat={centerLat}
            centerLon={centerLon}
            direction={flowDirection}
            magnitude={flowMagnitude}
            extent={plumeExtent}
          />
        )}

        {/* Source location */}
        <Marker position={[centerLat, centerLon]} icon={sourceIcon} />

        {/* Existing wells */}
        {Array.isArray(existingWells) && existingWells.filter(w => w && w.lat && w.lon).map((well) => {
          const isBreached = well.breachMonth !== null && well.breachMonth !== undefined && well.breachMonth <= selectedMonth;
          const hasExceedance = well.exceedanceProbability !== undefined && well.exceedanceProbability !== null;
          const icon = hasExceedance
            ? createExceedanceWellIcon(well.exceedanceProbability!, isBreached)
            : createWellIcon(isBreached ? '#ef4444' : '#10b981', isBreached, false);
          return (
            <Marker
              key={well.id}
              position={[well.lat, well.lon]}
              icon={icon}
              eventHandlers={{
                click: () => {
                  setSelectedWell(well);
                  onWellClick?.(well);
                },
              }}
            />
          );
        })}

        {/* Predicted/Recommended wells */}
        {showPredictions && Array.isArray(predictedWells) && predictedWells.filter(w => w && w.lat && w.lon).map((well) => (
          <Marker
            key={well.id}
            position={[well.lat, well.lon]}
            icon={createWellIcon('#f59e0b', false, true)}
            eventHandlers={{
              click: () => {
                setSelectedWell(well);
                onWellClick?.(well);
              },
            }}
          />
        ))}
      </MapContainer>

      {/* Enhanced Legend */}
      <div className="bg-gradient-to-r from-mono-900 via-mono-850 to-mono-900 p-3 border-t border-mono-700">
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs">
          {/* Source & Plume */}
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-gradient-to-br from-red-500 to-red-700 rounded-full border-2 border-white shadow-lg shadow-red-500/30"></div>
              <span className="text-mono-300">Source</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-5 h-3 bg-red-500/50 border-2 border-red-600 rounded-sm"></div>
              <span className="text-mono-300">Current Plume</span>
            </div>
          </div>

          {/* Concentration Zones */}
          {showAllContours && (
            <div className="flex items-center gap-3 border-l border-mono-700 pl-4">
              <span className="text-mono-500 text-[10px] uppercase">Concentration:</span>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ background: CONCENTRATION_COLORS.core.fill }}></div>
                <span className="text-mono-400">&gt;90%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ background: CONCENTRATION_COLORS.high.fill }}></div>
                <span className="text-mono-400">50-90%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ background: CONCENTRATION_COLORS.medium.fill }}></div>
                <span className="text-mono-400">10-50%</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-sm" style={{ background: CONCENTRATION_COLORS.low.fill }}></div>
                <span className="text-mono-400">&lt;10%</span>
              </div>
            </div>
          )}

          {/* Wells */}
          <div className="flex items-center gap-3 border-l border-mono-700 pl-4">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-emerald-500 rounded-full border border-white"></div>
              <span className="text-mono-400">Existing Well</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 bg-red-500 rounded-full border-2 border-red-400"></div>
              <span className="text-mono-400">Breached</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3.5 h-3.5 bg-amber-500 rounded-full border border-white shadow-sm shadow-amber-500/50"></div>
              <span className="text-mono-400">Recommended</span>
            </div>
          </div>

          {/* Exceedance probability (when predictions available) */}
          {existingWells.some(w => w.exceedanceProbability !== undefined) && (
            <div className="flex items-center gap-3 border-l border-mono-700 pl-4">
              <span className="text-mono-500 text-[10px] tracking-wider">P(MCL):</span>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-emerald-500 rounded-full"></div>
                <span className="text-mono-400">&lt;30%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-amber-500 rounded-full"></div>
                <span className="text-mono-400">30-70%</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="w-2.5 h-2.5 bg-red-500 rounded-full"></div>
                <span className="text-mono-400">&gt;70%</span>
              </div>
            </div>
          )}

          {/* Flow & Timeline */}
          {(showFlowField || trajectoryLine) && (
            <div className="flex items-center gap-3 border-l border-mono-700 pl-4">
              {showFlowField && (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-0.5 bg-gradient-to-r from-blue-500 to-blue-300"></div>
                  <span className="text-mono-400">GW Flow</span>
                </div>
              )}
              {trajectoryLine && trajectoryLine.length > 1 && (
                <div className="flex items-center gap-2">
                  <div className="w-5 h-0.5 border-t-2 border-dashed border-amber-500"></div>
                  <span className="text-mono-400">Migration Path</span>
                </div>
              )}
            </div>
          )}

          {selectedMonth > 6 && baselineContour && (
            <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
              <div className="w-5 h-3 border border-dashed border-slate-400 rounded-sm"></div>
              <span className="text-mono-400">6mo Baseline</span>
            </div>
          )}

          {/* Soil Survey Overlay */}
          {showSoilOverlay && (
            <div className="flex items-center gap-2 border-l border-mono-700 pl-4">
              <div className="w-5 h-3 bg-gradient-to-r from-green-700/60 to-yellow-600/60 rounded-sm border border-green-500/50"></div>
              <span className="text-mono-400">USDA Soil Survey</span>
            </div>
          )}
        </div>
      </div>

      {/* Selected well info panel */}
      {selectedWell && (
        <div className="bg-mono-800 p-4 border-t border-mono-700">
          <div className="flex justify-between items-start">
            <div>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${selectedWell.type === 'existing' ? 'bg-emerald-500' : 'bg-amber-500'}`}></div>
                <p className="text-mono-100 font-semibold">{selectedWell.name}</p>
              </div>
              <p className="text-mono-500 text-xs mt-1">
                {selectedWell.type === 'existing' ? 'Existing Monitoring Well' : 'Recommended Installation'}
              </p>
            </div>
            <button
              onClick={() => setSelectedWell(null)}
              className="text-mono-500 hover:text-mono-300 text-xl leading-none"
            >
              ×
            </button>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            {selectedWell.depth && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Depth</div>
                <div className="text-sm font-mono text-mono-200">{selectedWell.depth} ft</div>
              </div>
            )}
            {selectedWell.concentration !== undefined && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Concentration</div>
                <div className="text-sm font-mono text-mono-200">{(selectedWell.concentration / 1000).toFixed(3)} mg/L</div>
              </div>
            )}
            {selectedWell.installCost && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Install Cost</div>
                <div className="text-sm font-mono text-emerald-400">${selectedWell.installCost.toLocaleString()}</div>
              </div>
            )}
            {selectedWell.breachMonth !== null && selectedWell.breachMonth !== undefined && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Breach</div>
                <div className={`text-sm font-mono ${selectedWell.breachMonth <= selectedMonth ? 'text-red-400' : 'text-amber-400'}`}>
                  {selectedWell.breachMonth <= selectedMonth ? `Month ${selectedWell.breachMonth} (Breached)` : `Est. Month ${selectedWell.breachMonth}`}
                </div>
              </div>
            )}
            {selectedWell.exceedanceProbability !== undefined && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">P(Exceed MCL)</div>
                <div className={`text-sm font-mono font-bold ${
                  selectedWell.exceedanceProbability > 0.7 ? 'text-red-400'
                    : selectedWell.exceedanceProbability > 0.3 ? 'text-amber-400'
                    : 'text-emerald-400'
                }`}>
                  {Math.round(selectedWell.exceedanceProbability * 100)}%
                </div>
              </div>
            )}
            {selectedWell.predictedConcMgl !== undefined && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Predicted Conc</div>
                <div className={`text-sm font-mono ${
                  selectedWell.mclMgl && selectedWell.predictedConcMgl > selectedWell.mclMgl
                    ? 'text-red-400' : 'text-mono-200'
                }`}>
                  {selectedWell.predictedConcMgl < 0.001
                    ? selectedWell.predictedConcMgl.toExponential(2)
                    : selectedWell.predictedConcMgl < 1
                    ? selectedWell.predictedConcMgl.toFixed(4)
                    : selectedWell.predictedConcMgl.toFixed(2)} mg/L
                </div>
              </div>
            )}
            {selectedWell.concTrend && (
              <div className="bg-mono-900 rounded px-2 py-1.5">
                <div className="text-[10px] text-mono-500 uppercase">Trend</div>
                <div className={`text-sm font-mono ${
                  selectedWell.concTrend === 'decreasing' ? 'text-emerald-400'
                    : selectedWell.concTrend === 'increasing' ? 'text-red-400'
                    : 'text-amber-400'
                }`}>
                  {selectedWell.concTrend === 'decreasing' ? '↓ Decreasing'
                    : selectedWell.concTrend === 'increasing' ? '↑ Increasing'
                    : '→ Stable'}
                </div>
              </div>
            )}
          </div>
          <div className="text-[10px] text-mono-600 mt-2">
            Coordinates: {selectedWell.lat.toFixed(6)}, {selectedWell.lon.toFixed(6)}
          </div>
        </div>
      )}
    </div>
  );
}

// Helper function to get cardinal direction from degrees
function getCardinalDirection(degrees: number): string {
  const directions = ['N', 'NNE', 'NE', 'ENE', 'E', 'ESE', 'SE', 'SSE', 'S', 'SSW', 'SW', 'WSW', 'W', 'WNW', 'NW', 'NNW'];
  const index = Math.round(degrees / 22.5) % 16;
  return directions[index];
}
